import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useToken } from '@app/hooks';
import { useMyProfile } from '@app/hooks/user/myprofile';
import CIUtils from '@/utils/ciutils';
import { useTranslation } from 'react-i18next';
import { getSetting, useSetting } from '@app/hooks/user/mysetting';
import { SETTING_TYPE_SYSTEM } from '@app/constants/SETTING';
import { useFetcherPublic } from '@app/hooks/fetcher';
import { VITE_APP_API_MEDIA_URL } from '@/app/constants/APIKEYS';
import { IMAGES } from '@/app/constants';
import { useExchangeStore } from '@app/hooks/trade/exchangeStore';
import { useTickerStore } from '@app/hooks/ticketStore';
import { useCopyPortfolioStore } from '@app/hooks/copyTrading/copyPortfolioStore';

// Icons from local assets
const closeIconUrl = '/assets/newHome/menu/close-icon.svg';
const walletIconUrl = '/assets/newHome/menu/wallet-icon.svg';
const graphIconUrl = '/assets/newHome/menu/graph-icon.svg';
const swapIconUrl = '/assets/newHome/menu/swap-icon.svg';
const moreIconUrl = '/assets/newHome/menu/more-icon.svg';
const eventIconUrl = '/assets/newHome/menu/event-icon.svg';
const chatIconUrl = '/assets/newHome/menu/chat-icon.svg';
const searchIconUrl = '/assets/newHome/menu/search-icon.svg';
const searchIcon2Url = '/assets/newHome/menu/search-icon-2.svg';
const arrowIconUrl = '/assets/newHome/menu/arrow-icon.svg';
const settingIconUrl = '/assets/newHome/menu/setting-icon.svg';

interface MobileMenuDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MenuItem {
  id: string;
  labelKey: string;
  icon: string;
  route?: string;
  subItems?: SubMenuItem[];
}

interface SubMenuItem {
  labelKey: string;
  route: string;
}

// 비로그인 상태 메뉴
const menuItemsNotLoggedIn: MenuItem[] = [
  {
    id: 'crypto',
    labelKey: 'Crypto',
    icon: walletIconUrl,
    subItems: [{ labelKey: 'Buy/Sell Crypto', route: '/buysell-crypto' }],
  },
  {
    id: 'markets',
    labelKey: 'Markets',
    icon: graphIconUrl,
    subItems: [
      { labelKey: 'Markets Overview', route: '/markets' },
      { labelKey: 'Fear & Greed Index', route: '/fear-greed-index' },
    ],
  },
  {
    id: 'futures',
    labelKey: 'Futures',
    icon: swapIconUrl,
    subItems: [
      { labelKey: 'USDT Perpetuals', route: '/trade/2' },
      { labelKey: 'demoTrading', route: '/demo-trade/2' },
      { labelKey: 'Copy Trading', route: '/copy-trading' },
      { labelKey: 'Trading Bots', route: '/trading-bots' },
    ],
  },
  {
    id: 'more',
    labelKey: 'More',
    icon: moreIconUrl,
    subItems: [
      { labelKey: 'About Us', route: '/about-us' },
      { labelKey: 'Institutional', route: '/institutional' },
      { labelKey: 'Terms and Conditions', route: '/terms-and-conditions' },
      { labelKey: 'Guide', route: '/guide' },
      { labelKey: 'Notice Center', route: '/notice' },
    ],
  },
  {
    id: 'event-rewards',
    labelKey: 'Event Rewards',
    icon: eventIconUrl,
    route: '/reward',
  },
  {
    id: 'customer-service',
    labelKey: 'Customer Service',
    icon: chatIconUrl,
    route: '/support',
  },
];

// 로그인 상태 메뉴 (비로그인과 동일하게 서브메뉴 포함)
const menuItemsLoggedIn: MenuItem[] = [
  {
    id: 'crypto',
    labelKey: 'Crypto',
    icon: walletIconUrl,
    subItems: [{ labelKey: 'Buy/Sell Crypto', route: '/buysell-crypto' }],
  },
  {
    id: 'markets',
    labelKey: 'Markets',
    icon: graphIconUrl,
    subItems: [
      { labelKey: 'Markets Overview', route: '/markets' },
      { labelKey: 'Fear & Greed Index', route: '/fear-greed-index' },
    ],
  },
  {
    id: 'futures',
    labelKey: 'Futures',
    icon: swapIconUrl,
    subItems: [
      { labelKey: 'USDT Perpetuals', route: '/trade/2' },
      { labelKey: 'demoTrading', route: '/demo-trade/2' },
      { labelKey: 'Copy Trading', route: '/copy-trading' },
      { labelKey: 'Trading Bots', route: '/trading-bots' },
    ],
  },
  {
    id: 'more',
    labelKey: 'More',
    icon: moreIconUrl,
    subItems: [
      { labelKey: 'About Us', route: '/about-us' },
      { labelKey: 'Institutional', route: '/institutional' },
      { labelKey: 'Terms and Conditions', route: '/terms-and-conditions' },
      { labelKey: 'Guide', route: '/guide' },
      { labelKey: 'Notice Center', route: '/notice' },
    ],
  },
  {
    id: 'my-page',
    labelKey: 'My Page',
    icon: settingIconUrl,
    subItems: [
      { labelKey: 'Asset', route: '/asset' },
      { labelKey: 'Account & Security', route: '/account' },
      { labelKey: 'referralProgram', route: '/affiliate-program' },
      // { labelKey: 'competitionHistory', route: '/trading-competition-user' }
      { labelKey: 'competitionHistory', route: '/trade-competition/1' },
    ],
  },
  {
    id: 'event-rewards',
    labelKey: 'Event Rewards',
    icon: eventIconUrl,
    route: '/reward',
  },
  {
    id: 'customer-service',
    labelKey: 'Customer Service',
    icon: chatIconUrl,
    route: '/support',
  },
];

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9998;
  display: ${(props) => (props.$isOpen ? 'block' : 'none')};
`;

const DrawerContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  height: 100dvh; /* 모바일 브라우저 동적 뷰포트 높이 */
  background: linear-gradient(
    180deg,
    rgba(10, 19, 33, 1) 0%,
    rgba(15, 35, 31, 1) 100%
  );
  z-index: 9999;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  transform: ${(props) =>
    props.$isOpen ? 'translateX(0)' : 'translateX(-100%)'};
  transition: transform 0.3s ease;
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: max(40px, env(safe-area-inset-bottom, 40px));
  display: flex;
  flex-direction: column;
`;

const NavBar = styled.div`
  position: relative;
  top: 0;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  z-index: 10000;
  background: transparent;
  flex-shrink: 0;
`;

const NavBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NavBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const LoginButton = styled.button`
  border: 1px solid #8d8d8d;
  border-radius: 12px;
  padding: 8px 20px;
  background: transparent;
  color: #ffffff;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.28px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const SignupButton = styled.button`
  border: none;
  border-radius: 12px;
  padding: 8px 20px;
  background: #07d993;
  color: #0d1117;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.28px;
  cursor: pointer;
  box-shadow: 0px 12px 30px 0px rgba(7, 217, 147, 0.5);
  transition: all 0.2s;

  &:hover {
    background: #06c585;
  }
`;

const TradeButton = styled.button`
  border: none;
  border-radius: 12px;
  padding: 8px 20px;
  width: 80px;
  flex-shrink: 0;
  background: #07d993;
  color: #0d1117;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.28px;
  cursor: pointer;
  box-shadow: 0px 12px 30px 0px rgba(7, 217, 147, 0.5);
  transition: all 0.2s;

  &:hover {
    background: #06c585;
  }
`;

const CloseButton = styled.button`
  width: 20px;
  height: 20px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CloseIcon = styled.img`
  width: 100%;
  height: 100%;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UserAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #ffffff;
  flex-shrink: 0;
  object-fit: cover;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const UserName = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.28px;
  margin: 0;
  min-width: 0;
  word-break: break-word;
`;

const UserUID = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.24px;
  margin: 0;
`;

const SearchSection = styled.div`
  padding: 10px 16px;
  position: relative;
`;

const SearchBox = styled.div`
  border: 1px solid #353535;
  border-radius: 12px;
  height: 36px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  background: transparent;
  cursor: pointer;
  position: relative;
`;

const SearchIcon = styled.img`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #b3b5bd;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  letter-spacing: -0.24px;
  cursor: pointer;

  &::placeholder {
    color: #b3b5bd;
  }
`;

const SearchDropdown = styled.div<{ $isOpen: boolean }>`
  display: ${(props) => (props.$isOpen ? 'block' : 'none')};
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: #0d1117;
  border: 1px solid #353535;
  border-radius: 12px;
  padding: 16px;
  max-height: 500px;
  overflow-y: auto;
  z-index: 1000;
`;

const TopSearchedSection = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #353535;
`;

const SectionTitleText = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.28px;
  margin: 0;
`;

const CoinList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CoinItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const CoinInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CoinHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CoinIcon = styled.img`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const CoinSymbol = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;
`;

const CoinSymbolText = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  color: #ffffff;
  letter-spacing: -0.84px;
  margin: 0;
  height: 15px;
`;

const CoinQuote = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  color: #b3b5bd;
  letter-spacing: -0.24px;
  margin: 0;
`;

const CoinType = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  color: #777986;
  letter-spacing: -0.24px;
  margin: 0;
`;

const CoinPrice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const PriceValue = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.84px;
  margin: 0;
  white-space: pre-wrap;
`;

const PriceChange = styled.p<{ $isPositive: boolean }>`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: ${(props) => (props.$isPositive ? '#4caf50' : '#f44336')};
  letter-spacing: -0.72px;
  margin: 0;
  text-align: right;
  white-space: pre-wrap;
`;

const MasterTradersSection = styled.div`
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #353535;
`;

const MasterTradersHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const MasterTradersTitle = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.28px;
  margin: 0;
`;

const ArrowDownIcon = styled.img`
  width: 16px;
  height: 16px;
  transform: rotate(270deg);
`;

const TraderList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TraderCard = styled.div`
  background: #0c2323;
  border-radius: 16px;
  padding: 12px 16px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TraderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const TraderAvatar = styled.div`
  width: 36px;
  height: 36px;
  background: #ffffff;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const TraderAvatarImage = styled.img`
  width: 24px;
  height: 20px;
`;

const TraderDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TraderName = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  color: #ffffff;
  letter-spacing: -0.28px;
  margin: 0;
`;

const TraderFollowers = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  color: #9496a1;
  letter-spacing: -0.24px;
  margin: 0;
`;

const TraderPnL = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
`;

const TraderPnLLabel = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 10px;
  color: #b3b5bd;
  letter-spacing: -0.6px;
  margin: 0;
  white-space: pre-wrap;
`;

const TraderPnLValue = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.72px;
  margin: 0;
`;

const MenuSection = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const MenuItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MenuItemButton = styled.button<{ $isExpanded?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 0;
  background: transparent;
  border: none;
  cursor: pointer;
  width: 100%;
`;

const MenuIcon = styled.img`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: block;
  object-fit: contain;
`;

const MenuLabel = styled.p`
  flex: 1;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #ffffff;
  letter-spacing: -0.28px;
  text-align: left;
  margin: 0;
  line-height: normal;
`;

const ArrowIcon = styled.img<{ $isExpanded?: boolean }>`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  transform: ${(props) => (props.$isExpanded ? 'scaleY(-1)' : 'scaleY(1)')};
  transition: transform 0.2s;
`;

const SubMenuContainer = styled.div<{ $isExpanded: boolean }>`
  display: ${(props) => (props.$isExpanded ? 'flex' : 'none')};
  flex-direction: column;
  gap: 16px;
  padding: 0 34px 6px;
`;

const SubMenuItem = styled.button`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #b3b5bd;
  height: 21px;
  letter-spacing: -0.28px;
  background: transparent;
  border: none;
  cursor: pointer;
  line-height: normal;
  text-align: left;
  padding: 0;
  transition: color 0.2s;

  &:hover {
    color: #ffffff;
  }
`;

const SettingsSection = styled.div<{ $isLoggedIn?: boolean }>`
  padding: ${(props) => (props.$isLoggedIn ? '20px 16px 40px' : '40px 16px')};
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: ${(props) => (props.$isLoggedIn ? 'flex-end' : 'flex-start')};
  position: relative;
  width: 100%;
  margin-top: auto;
`;

const SettingGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const SettingLabel = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.24px;
  margin: 0;
  text-align: left;
`;

const SettingSelect = styled.select`
  background: rgba(91, 93, 107, 0.3);
  border: none;
  border-radius: 8px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 12px;
  cursor: pointer;
  transition: background 0.2s;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #ffffff;
  letter-spacing: -0.28px;
  outline: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20'%3E%3Cpath fill='%23ffffff' d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 20px;
  padding-right: 40px;

  &:hover {
    background-color: rgba(91, 93, 107, 0.5);
  }

  option {
    background: #0d1117;
    color: #ffffff;
  }
`;

const LogoutButton = styled.button`
  border: 1px solid #8d8d8d;
  border-radius: 12px;
  padding: 8px 20px;
  background: transparent;
  color: #b3b5bd;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: -0.28px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }
`;

const MobileMenuDrawer: React.FC<MobileMenuDrawerProps> = ({
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const { data: token, removeTokens } = useToken();
  const { data: myinfo } = useMyProfile();
  const { t, i18n } = useTranslation();
  const { data: setting, updateSetting } = useSetting(SETTING_TYPE_SYSTEM);
  const { data: currencyResult } = useFetcherPublic('/system/currency');
  const currency = getSetting(setting, 'CURRENCY', 'USD');
  const currencyOptions = currencyResult?.result?.result || [];
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const touchEndY = useRef<number>(0);
  const { EPs } = useExchangeStore();
  const { connect, data: tickerData, closeWS } = useTickerStore();
  const { portfolios, fetchPortfolios } = useCopyPortfolioStore();

  const isLoggedIn = !!token;

  const menuItems = isLoggedIn ? menuItemsLoggedIn : menuItemsNotLoggedIn;

  const handleMenuClick = (item: MenuItem) => {
    if (item.subItems && item.subItems.length > 0) {
      // 서브메뉴가 있으면 토글
      const newExpanded = new Set(expandedMenus);
      if (newExpanded.has(item.id)) {
        newExpanded.delete(item.id);
      } else {
        newExpanded.add(item.id);
      }
      setExpandedMenus(newExpanded);
    } else if (item.route) {
      navigate(item.route);
      onClose();
    }
  };

  const handleSubMenuClick = (route: string) => {
    navigate(route);
    onClose();
  };

  const handleLoginClick = () => {
    navigate('/login');
    onClose();
  };

  const handleSignupClick = () => {
    navigate('/signup');
    onClose();
  };

  const handleTradeClick = () => {
    navigate('/trade/2');
    onClose();
  };

  const handleLogout = () => {
    removeTokens();
    navigate('/');
    onClose();
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    i18n.changeLanguage(e.target.value);
  };

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateSetting('CURRENCY', e.target.value);
  };

  // 스와이프로 메뉴 닫기
  const handleTouchStart = (e: React.TouchEvent) => {
    // 셀렉트박스 터치 시 무시
    const target = e.target as HTMLElement;
    if (target.tagName === 'SELECT' || target.closest('select')) return;

    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
    touchEndY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    // 터치 시작이 없었거나 이동이 없었으면 무시 (단순 탭)
    if (touchStartX.current === 0 || touchEndX.current === 0) {
      touchStartX.current = 0;
      touchEndX.current = 0;
      touchStartY.current = 0;
      touchEndY.current = 0;
      return;
    }

    // 셀렉트박스 터치 시 무시
    const target = e.target as HTMLElement;
    if (target.tagName === 'SELECT' || target.closest('select')) {
      touchStartX.current = 0;
      touchEndX.current = 0;
      touchStartY.current = 0;
      touchEndY.current = 0;
      return;
    }

    const swipeDistanceX = Math.abs(touchStartX.current - touchEndX.current);
    const swipeDistanceY = Math.abs(touchStartY.current - touchEndY.current);
    const minSwipeDistance = 80;

    // 수평 스와이프가 수직보다 크고, 최소 거리 이상일 때만 닫기
    if (swipeDistanceX > swipeDistanceY && swipeDistanceX > minSwipeDistance) {
      onClose();
    }

    // 리셋
    touchStartX.current = 0;
    touchEndX.current = 0;
    touchStartY.current = 0;
    touchEndY.current = 0;
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setIsSearchOpen(false);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // 검색 영역 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        isSearchOpen &&
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsSearchOpen(false);
        setSearchQuery('');
      }
    };

    if (isSearchOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isSearchOpen]);

  // 티커 데이터 연결
  useEffect(() => {
    if (isSearchOpen && EPs && EPs.length > 0) {
      const symbols = EPs.map((ep: any) =>
        ep.pairs.replace('/', '').toLowerCase()
      );
      connect(symbols);
    }
    return () => {
      if (isSearchOpen) {
        closeWS();
      }
    };
  }, [isSearchOpen, EPs, connect, closeWS]);

  // 코인 데이터 포맷팅
  const formatPrice = (price: string): string => {
    const num = parseFloat(price);
    if (num >= 1000) {
      return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
    } else if (num >= 1) {
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } else {
      return num.toFixed(8).replace(/\.?0+$/, '');
    }
  };

  // 검색 필터링된 코인 목록 (기존 코드와 동일한 방식)
  const filteredCoins = useMemo(() => {
    if (!EPs || !searchQuery) {
      return [];
    }

    const filtered = EPs.filter((item: any) =>
      item?.symbol?.toLowerCase()?.includes(searchQuery?.toLowerCase())
    );

    return filtered.slice(0, 5).map((ep: any) => {
      const symbolKey = ep.pairs.replace('/', '').toLowerCase();
      const ticker = tickerData[symbolKey];

      return {
        symbol:
          ep.symbol?.replace('/', '').replace('USDT', '') ||
          ep.symbol?.split('/')[0] ||
          '',
        quote: ep.symbol?.split('/')[1] || 'USDT',
        type: 'USDT Perpetual',
        price: ticker ? formatPrice(ticker.c || '0') : '0',
        change: ticker ? parseFloat(ticker.P || '0') : 0,
        icon: ep.image || '',
        epId: ep.id,
      };
    });
  }, [EPs, searchQuery, tickerData]);

  // Top Searched 코인 목록 (거래량 기준 상위 6개) - 검색어가 없을 때만 표시
  const topSearchedCoins = useMemo(() => {
    if (
      searchQuery ||
      !EPs ||
      !tickerData ||
      Object.keys(tickerData).length === 0
    ) {
      return [];
    }

    const tickerArray = Object.entries(tickerData)
      .map(([key, data]: [string, any]) => ({
        symbol: key,
        ticker: data,
        ep: EPs.find(
          (ep: any) =>
            ep.pairs.replace('/', '').toLowerCase() === key.toLowerCase()
        ),
      }))
      .filter((item) => item.ep);

    return tickerArray
      .sort(
        (a, b) => parseFloat(b.ticker.q || '0') - parseFloat(a.ticker.q || '0')
      )
      .slice(0, 6)
      .map((item, index) => ({
        symbol:
          item.ticker.s?.replace('USDT', '') ||
          item.symbol.replace('usdt', '').toUpperCase(),
        quote: 'USDT',
        type: 'USDT Perpetual',
        price: formatPrice(item.ticker.c || '0'),
        change: parseFloat(item.ticker.P || '0'),
        icon: item.ep?.image || '',
        epId: item.ep?.id,
      }));
  }, [EPs, tickerData, searchQuery]);

  // Top 3 Master Traders - 기존 코드와 동일한 API 사용
  useEffect(() => {
    if (isSearchOpen) {
      // 기존 CopyTradingSection과 동일한 방식으로 Top 3 조회
      fetchPortfolios({
        sortBy: 'monthlyRoi' as any, // 월간 ROI 순위
        order: 'desc',
        status: 1, // Active 상태만
        page: 1,
        pageSize: 3, // Top 3만
      });
    }
  }, [isSearchOpen, fetchPortfolios]);

  const topTraders = useMemo(() => {
    if (!portfolios || portfolios.length === 0) {
      return [];
    }
    return portfolios.slice(0, 3).map((portfolio) => {
      const pnl = portfolio.totalPnl || 0;
      const pnlFormatted =
        pnl >= 0
          ? `+ ${pnl.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : `${pnl.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

      return {
        name: portfolio.leaderUsername || 'Unknown',
        followers: portfolio.currentFollowers || 0,
        totalPnL: pnlFormatted,
        avatar: portfolio.leaderProfileImage || portfolio.leaderAvatar || '',
      };
    });
  }, [portfolios]);

  const handleSearchBoxClick = () => {
    setIsSearchOpen(!isSearchOpen);
  };

  const handleCoinClick = (epId: number) => {
    navigate(`/trade/${epId}`);
    onClose();
  };

  return (
    <>
      <Overlay $isOpen={isOpen} onClick={onClose} />
      <DrawerContainer
        $isOpen={isOpen}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <NavBar>
          {isLoggedIn ? (
            <>
              <UserInfo>
                <UserAvatar
                  src={
                    myinfo?.profileimage
                      ? `${VITE_APP_API_MEDIA_URL}/${myinfo.profileimage}`
                      : IMAGES.avatar
                  }
                  alt="Profile"
                />
                <UserDetails>
                  <UserName>{myinfo?.nickname || 'User'}</UserName>
                  <UserUID>
                    UID : {myinfo?.id ? CIUtils.padId(myinfo.id) : '-'}
                  </UserUID>
                </UserDetails>
              </UserInfo>
              <NavBarRight>
                <TradeButton onClick={handleTradeClick}>
                  {t('Trade')}
                </TradeButton>
                <CloseButton onClick={onClose}>
                  <CloseIcon src={closeIconUrl} alt="Close" />
                </CloseButton>
              </NavBarRight>
            </>
          ) : (
            <>
              <NavBarLeft>
                <LoginButton onClick={handleLoginClick}>
                  {t('Login')}
                </LoginButton>
                <SignupButton onClick={handleSignupClick}>
                  {t('Sign up')}
                </SignupButton>
              </NavBarLeft>
              <CloseButton onClick={onClose}>
                <CloseIcon src={closeIconUrl} alt="Close" />
              </CloseButton>
            </>
          )}
        </NavBar>

        <SearchSection ref={searchRef}>
          <SearchBox onClick={handleSearchBoxClick}>
            <SearchIcon src={searchIcon2Url} alt="Search" />
            <SearchInput
              type="text"
              placeholder={t('Find Token')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClick={(e) => {
                e.stopPropagation();
                setIsSearchOpen(true);
              }}
            />
            <SearchDropdown $isOpen={isSearchOpen}>
              {searchQuery ? (
                // 검색 결과 표시 (기존 코드와 동일한 방식)
                <TopSearchedSection>
                  <SectionTitle>
                    <SectionTitleText>{t('Search')}</SectionTitleText>
                  </SectionTitle>
                  <CoinList>
                    {filteredCoins.length > 0 ? (
                      filteredCoins.map((coin, index) => (
                        <CoinItem
                          key={index}
                          onClick={() =>
                            coin.epId && handleCoinClick(coin.epId)
                          }
                        >
                          <CoinInfo>
                            <CoinHeader>
                              {coin.icon && (
                                <CoinIcon
                                  src={
                                    coin.icon.startsWith('http')
                                      ? coin.icon
                                      : `${VITE_APP_API_MEDIA_URL}/${coin.icon}`
                                  }
                                  alt={coin.symbol}
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              )}
                              <CoinSymbol>
                                <CoinSymbolText>{coin.symbol}</CoinSymbolText>
                                <CoinQuote>{coin.quote}</CoinQuote>
                              </CoinSymbol>
                            </CoinHeader>
                            <CoinType>{coin.type}</CoinType>
                          </CoinInfo>
                          <CoinPrice>
                            <PriceValue>{coin.price}</PriceValue>
                            <PriceChange $isPositive={coin.change >= 0}>
                              {coin.change >= 0 ? '+' : ''}
                              {coin.change.toFixed(2)}%
                            </PriceChange>
                          </CoinPrice>
                        </CoinItem>
                      ))
                    ) : (
                      <div
                        style={{
                          color: '#b3b5bd',
                          textAlign: 'center',
                          padding: '20px',
                        }}
                      >
                        {t('No results found')}
                      </div>
                    )}
                  </CoinList>
                </TopSearchedSection>
              ) : (
                // 검색어가 없을 때 Top Searched 표시
                <TopSearchedSection>
                  <SectionTitle>
                    <SectionTitleText>{t('Top Searched')}</SectionTitleText>
                  </SectionTitle>
                  <CoinList>
                    {topSearchedCoins.length > 0 ? (
                      topSearchedCoins.map((coin, index) => (
                        <CoinItem
                          key={index}
                          onClick={() =>
                            coin.epId && handleCoinClick(coin.epId)
                          }
                        >
                          <CoinInfo>
                            <CoinHeader>
                              {coin.icon && (
                                <CoinIcon
                                  src={
                                    coin.icon.startsWith('http')
                                      ? coin.icon
                                      : `${VITE_APP_API_MEDIA_URL}/${coin.icon}`
                                  }
                                  alt={coin.symbol}
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              )}
                              <CoinSymbol>
                                <CoinSymbolText>{coin.symbol}</CoinSymbolText>
                                <CoinQuote>{coin.quote}</CoinQuote>
                              </CoinSymbol>
                            </CoinHeader>
                            <CoinType>{coin.type}</CoinType>
                          </CoinInfo>
                          <CoinPrice>
                            <PriceValue>{coin.price}</PriceValue>
                            <PriceChange $isPositive={coin.change >= 0}>
                              {coin.change >= 0 ? '+' : ''}
                              {coin.change.toFixed(2)}%
                            </PriceChange>
                          </CoinPrice>
                        </CoinItem>
                      ))
                    ) : (
                      <div
                        style={{
                          color: '#b3b5bd',
                          textAlign: 'center',
                          padding: '20px',
                        }}
                      >
                        {t('Loading...')}
                      </div>
                    )}
                  </CoinList>
                </TopSearchedSection>
              )}
              <MasterTradersSection>
                <MasterTradersHeader>
                  <MasterTradersTitle>
                    {t('Top 3 Master Traders')}
                  </MasterTradersTitle>
                  <ArrowDownIcon src={arrowIconUrl} alt="Arrow" />
                </MasterTradersHeader>
                <TraderList>
                  {topTraders.length > 0 ? (
                    topTraders.map((trader, index) => (
                      <TraderCard key={index}>
                        <TraderInfo>
                          <TraderAvatar>
                            {trader.avatar ? (
                              <TraderAvatarImage
                                src={
                                  trader.avatar.startsWith('http')
                                    ? trader.avatar
                                    : `${VITE_APP_API_MEDIA_URL}/${trader.avatar}`
                                }
                                alt={trader.name}
                                onError={(e) => {
                                  e.currentTarget.src =
                                    '/assets/newHome/menu/wallet-icon.svg';
                                }}
                              />
                            ) : (
                              <TraderAvatarImage
                                src="/assets/newHome/menu/wallet-icon.svg"
                                alt={trader.name}
                              />
                            )}
                          </TraderAvatar>
                          <TraderDetails>
                            <TraderName>{trader.name}</TraderName>
                            <TraderFollowers>
                              {trader.followers.toLocaleString()}{' '}
                              {t('Followers')}
                            </TraderFollowers>
                          </TraderDetails>
                        </TraderInfo>
                        <TraderPnL>
                          <TraderPnLLabel>{t('Total PnL')}</TraderPnLLabel>
                          <TraderPnLValue>{trader.totalPnL}</TraderPnLValue>
                        </TraderPnL>
                      </TraderCard>
                    ))
                  ) : (
                    <div
                      style={{
                        color: '#b3b5bd',
                        textAlign: 'center',
                        padding: '20px',
                      }}
                    >
                      {t('Loading...')}
                    </div>
                  )}
                </TraderList>
              </MasterTradersSection>
            </SearchDropdown>
          </SearchBox>
        </SearchSection>

        <MenuSection>
          <MenuItemContainer>
            {menuItems.map((item) => (
              <div key={item.id}>
                <MenuItemButton
                  onClick={() => handleMenuClick(item)}
                  $isExpanded={expandedMenus.has(item.id)}
                >
                  <MenuIcon src={item.icon} alt={t(item.labelKey)} />
                  <MenuLabel>{t(item.labelKey)}</MenuLabel>
                  {item.subItems && item.subItems.length > 0 && (
                    <ArrowIcon
                      src={arrowIconUrl}
                      alt="Arrow"
                      $isExpanded={expandedMenus.has(item.id)}
                    />
                  )}
                </MenuItemButton>
                {item.subItems && item.subItems.length > 0 && (
                  <SubMenuContainer $isExpanded={expandedMenus.has(item.id)}>
                    {item.subItems.map((subItem, index) => (
                      <SubMenuItem
                        key={index}
                        onClick={() => handleSubMenuClick(subItem.route)}
                      >
                        {t(subItem.labelKey)}
                      </SubMenuItem>
                    ))}
                  </SubMenuContainer>
                )}
              </div>
            ))}
          </MenuItemContainer>
        </MenuSection>

        <SettingsSection $isLoggedIn={isLoggedIn}>
          <SettingGroup>
            <SettingLabel>{t('Language')}</SettingLabel>
            <SettingSelect
              value={i18n.language}
              onChange={handleLanguageChange}
            >
              <option value="en">English</option>
              <option value="ko">한국어</option>
            </SettingSelect>
          </SettingGroup>
          <SettingGroup>
            <SettingLabel>{t('Currency')}</SettingLabel>
            <SettingSelect value={currency} onChange={handleCurrencyChange}>
              {currencyOptions.length > 0 ? (
                currencyOptions.map((currencyOption: any) => (
                  <option key={currencyOption.code} value={currencyOption.code}>
                    {currencyOption.name}
                  </option>
                ))
              ) : (
                <option value="USD">USD</option>
              )}
            </SettingSelect>
          </SettingGroup>
          {isLoggedIn && (
            <LogoutButton onClick={handleLogout}>{t('Logout')}</LogoutButton>
          )}
        </SettingsSection>
      </DrawerContainer>
    </>
  );
};

export default MobileMenuDrawer;
